import { useHotkeys } from "@tanstack/react-hotkeys";
import type {
  CellSelectionDirection,
  ReactTable,
  Row,
  RowData,
} from "@tanstack/react-table";
import { useLayoutEffect, type RefObject } from "react";
import { flushSync } from "react-dom";

import type { DataTableFeatures } from "../features";

const WIDGET_SELECTOR =
  "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])";

type GridTable = ReactTable<DataTableFeatures, RowData>;

type GridRow = Row<DataTableFeatures, RowData>;

type Edge = "first" | "last";

interface UseGridCellNavigationOptions<T extends HTMLElement = HTMLElement> {
  table: GridTable;
  containerRef: RefObject<T | null>;
  enabled: boolean;
  onEscape?: () => void;
  onRowActivate?: (item: RowData) => void;
}

function pickEdge<TItem>(items: TItem[], edge: Edge): TItem | undefined {
  return edge === "first" ? items[0] : items.at(-1);
}

function getFocusTarget(cell: HTMLTableCellElement): HTMLElement {
  const widgets = cell.querySelectorAll<HTMLElement>(WIDGET_SELECTOR);
  return widgets.length === 1 ? widgets[0] : cell;
}

function clampFocusedCell(table: GridTable): boolean {
  const active = table.state.cellSelection.at(-1);
  if (!active) return false;

  const rows = table.getRowModel().rows;
  const columns = table.getVisibleLeafColumns();
  const hasRow = rows.some((row) => row.id === active.anchorRowId);
  const hasColumn = columns.some(
    (column) => column.id === active.anchorColumnId
  );
  if (hasRow && hasColumn) return false;

  if (rows.length === 0 || columns.length === 0) {
    table.resetCellSelection(true);
  } else {
    table.setFocusedCell(
      hasRow ? active.anchorRowId : rows[0].id,
      hasColumn ? active.anchorColumnId : columns[0].id
    );
  }
  return true;
}

function applyRovingTabIndex(
  grid: HTMLElement,
  activeTarget: HTMLElement | undefined
): void {
  for (const cell of grid.querySelectorAll<HTMLTableCellElement>("th, td")) {
    const target = getFocusTarget(cell);
    if (target !== cell) {
      cell.removeAttribute("tabindex");
      target.tabIndex = target === activeTarget ? 0 : -1;
      continue;
    }

    cell.tabIndex = cell === activeTarget ? 0 : -1;
    if (cell === activeTarget) continue;
    for (const widget of cell.querySelectorAll<HTMLElement>(WIDGET_SELECTOR)) {
      widget.tabIndex = -1;
    }
  }
}

function useGridCellNavigation<T extends HTMLElement = HTMLElement>({
  table,
  containerRef,
  enabled,
  onEscape,
  onRowActivate,
}: UseGridCellNavigationOptions<T>): void {
  const getGrid = () =>
    containerRef.current?.querySelector<HTMLElement>('[role="grid"]') ?? null;

  const getActiveBodyCell = (): HTMLTableCellElement | undefined => {
    const cells = Array.from(
      getGrid()?.querySelectorAll<HTMLTableCellElement>("tbody > tr > td") ?? []
    );
    const focusedCellId = table.getFocusedCell()?.id;
    return (
      cells.find((cell) => cell.dataset.cellId === focusedCellId) ?? cells[0]
    );
  };

  const getFocusedHeaderCell = (): HTMLTableCellElement | null =>
    document.activeElement?.closest<HTMLTableCellElement>("thead th") ?? null;

  useLayoutEffect(() => {
    if (!enabled) return;
    const grid = getGrid();
    if (!grid || clampFocusedCell(table)) return;

    const activeCell = getActiveBodyCell();
    applyRovingTabIndex(
      grid,
      activeCell ? getFocusTarget(activeCell) : undefined
    );
  });

  const navigate = (update: () => void) => {
    flushSync(update);
    const activeCell = getActiveBodyCell();
    if (activeCell) getFocusTarget(activeCell).focus();
  };

  const focusBodyCell = (rowId: string, columnId: string) =>
    navigate(() => table.setFocusedCell(rowId, columnId));

  const focusHeaderCell = (headerCell: HTMLTableCellElement, index: number) => {
    const headerCells = Array.from(headerCell.closest("tr")?.cells ?? []);
    const next =
      headerCells[Math.min(Math.max(index, 0), headerCells.length - 1)];
    if (next) getFocusTarget(next).focus();
  };

  const move = (direction: CellSelectionDirection) => {
    const headerCell = getFocusedHeaderCell();
    if (!headerCell) {
      navigate(() => table.moveCellSelection(direction));
      return;
    }

    if (direction === "left" || direction === "right") {
      const step = direction === "left" ? -1 : 1;
      focusHeaderCell(headerCell, headerCell.cellIndex + step);
      return;
    }

    const firstRow = table.getRowModel().rows[0];
    if (!firstRow) return;
    const cells = firstRow.getVisibleCells();
    const cell = cells[Math.min(headerCell.cellIndex, cells.length - 1)];
    focusBodyCell(firstRow.id, cell.column.id);
  };

  const focusRowEdge = (row: GridRow | undefined, edge: Edge) => {
    const cell = row && pickEdge(row.getVisibleCells(), edge);
    if (row && cell) focusBodyCell(row.id, cell.column.id);
  };

  const moveToRowEdge = (edge: Edge) => {
    const headerCell = getFocusedHeaderCell();
    if (headerCell) {
      focusHeaderCell(headerCell, edge === "first" ? 0 : Infinity);
      return;
    }
    focusRowEdge(
      table.getFocusedCell()?.row ?? table.getRowModel().rows[0],
      edge
    );
  };

  const moveToGridCorner = (edge: Edge) =>
    focusRowEdge(pickEdge(table.getRowModel().rows, edge), edge);

  const activateFocusedRow = (event: KeyboardEvent) => {
    const isBodyCell =
      event.target instanceof HTMLElement &&
      event.target.matches("tbody > tr > td");
    const row = table.getFocusedCell()?.row;
    if (isBodyCell && row) onRowActivate?.(row.original);
  };

  useHotkeys(
    [
      { hotkey: "ArrowUp", callback: () => move("up") },
      { hotkey: "ArrowDown", callback: () => move("down") },
      { hotkey: "ArrowLeft", callback: () => move("left") },
      { hotkey: "ArrowRight", callback: () => move("right") },
      { hotkey: "Home", callback: () => moveToRowEdge("first") },
      { hotkey: "End", callback: () => moveToRowEdge("last") },
      { hotkey: "Control+Home", callback: () => moveToGridCorner("first") },
      { hotkey: "Control+End", callback: () => moveToGridCorner("last") },
      { hotkey: "Meta+Home", callback: () => moveToGridCorner("first") },
      { hotkey: "Meta+End", callback: () => moveToGridCorner("last") },
      {
        hotkey: "Enter",
        callback: activateFocusedRow,
        options: { preventDefault: false },
      },
      {
        hotkey: "Escape",
        callback: () => onEscape?.(),
        options: { ignoreInputs: false, preventDefault: false },
      },
    ],
    {
      target: containerRef,
      enabled,
      ignoreInputs: true,
      stopPropagation: false,
    }
  );
}

export { useGridCellNavigation };
export type { UseGridCellNavigationOptions };
